/**
 * @module PodcastManager Manage the podcasts
 */
import noop from 'lodash/noop'
import Dexie from 'dexie'
import to from '@/helpers/to'

import config from './config.json'

class PodcastManager {
  static DATABASE_NAME = 'vino_podcast_database'

  static DATABASE_VERSION = 1

  static OBJECT_STORE_NAME = 'podcasts'

  static INDEX_NAME = 'podcast'

  static CORS_URL = 'https://podcast.vinoways.fr/'

  constructor(type = 'anchor') {
    /**
     * @type {Dexie}
     */
    this.db = null
    /**
     * @type {Dexie.Table}
     */
    this.dbTable = null
    this.accessToken = null

    this.config = config

    this.type = type

    this.initPromise = this.init()
  }

  /**
   * Initialize the podcast storage manager
   * @returns {void}
   */
  async init() {
    const db = new Dexie(PodcastManager.DATABASE_NAME)

    // Define the version and the indexed properties
    db.version(PodcastManager.DATABASE_VERSION).stores({
      [PodcastManager.OBJECT_STORE_NAME]: 'url'
    })
    return new Promise((resolve, reject) => {
      db.open()
        .then(() => {
          this.db = db
          this.dbTable = db[PodcastManager.OBJECT_STORE_NAME]
          resolve(this.db)
        })
        .catch(reject)
    })
  }

  /**
   * Set the authentification bearer token for spotify
   * @returns {void}
   */
  async setSpotifyAccessToken() {
    const conf = this.config

    let headers = new Headers();
    let urlencoded = new URLSearchParams();
    headers.append("Authorization", `Basic ${btoa(`${conf.spotify_client_id}:${conf.spotify_client_secret}`)}`);
    headers.append("Content-Type", "application/x-www-form-urlencoded");
    urlencoded.append("grant_type", "client_credentials");

    const req = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: headers,
      body: urlencoded,
      redirect: 'follow'
    })

    const res = await req.text()
    this.accessToken = JSON.parse(res)['access_token']
  }

  /**
   * Test if a podcast is stored in the indexeddb
   * @param {string} url
   * @returns {boolean}
   */
  async has(url) {
    await this.initPromise

    const [error, result] = await to(this.getPodcast(url))

    if (error || !result) return false

    return true
  }

  /**
   * Store a podcast
   * @param {string} name
   * @param {Promise} blob
   * @returns {void}
   */
  async storePodcast(url, id, blob) {
    await this.initPromise

    await this.db.transaction('rw', this.dbTable, async () => {
      await this.dbTable.put({ url: url, id: id, podcast: blob }, url)
    })
  }

  /**
   * Get a podcast from the local indexeddb
   * @param {string} name
   * @returns {Promise<Blob | null>}
   */
  async getPodcast(url) {
    await this.initPromise

    try {
      const { podcast } = await this.dbTable.get({ url })

      return podcast
    } catch (error) {
      return null
    }
  }

  /**
   * @param {string} podcastUrl The podcast url as defined in contentful
   * @param {string} trackurl The track url used for fetching the audio
   * @param {string} podcastId
   * @param {boolean} force Foce the download ?
   * @param {function} onProgress Callback used to display download progression
   * @returns {Blob}
   */
  async dispatchPodcastDownload(
    podcastUrl,
    trackUrl = null,
    podcastId = null,
    force = false,
    onProgress
  ) {
    if (!force) {
      // get existant podcast
      const [existError, existPodcast] = await to(this.getPodcast(podcastUrl))
      if (existPodcast && !existError) return existPodcast
    }

    let response1,
      resJson,
      blob = null

    if (trackUrl) {
      switch (this.type) {
        case 'soundcloud':
          break

        case 'anchor':
          // call trackUrl to get the download json url
          response1 = await fetch(trackUrl)
          resJson = await response1.json()
          if ('episode' in resJson && 'episodeEnclosureUrl' in resJson['episode']) {
            // get the stream as blob
            let fetchUrl =
              'https://podcast.vinoways.fr/' + resJson['episode']['episodeEnclosureUrl']
            // if (process.env.VUE_APP_ENV == 'development')
            //   fetchUrl = 'https://vinovibes-cors-anywhere.herokuapp.com/' + resJson['episode']['episodeEnclosureUrl']
            // else fetchUrl = resJson['episode']['episodeEnclosureUrl']
            blob = await this._fetchPodcastWithMonitor(fetchUrl, onProgress)
          }
          break
        default:
          break
      }
      if (blob) {
        // store it
        await this.storePodcast(podcastUrl, podcastId, blob)
        return blob
      }
    }
  }

  /**
   * Will fetch the podcast from url and tranform it to a mp3 blob.
   * @param {string} url
   * @param {Function} monitor
   * @returns {Blob}
   */
  async _fetchPodcastWithMonitor(url, monitor = null) {
    const response = await fetch(url)
    const callback = monitor && typeof monitor === 'function' ? monitor : noop

    const contentSize = Number(response.headers.get('Content-Length'))
    const reader = response.body.getReader()

    let received = 0
    const chunks = []

    // eslint-disable-next-line
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        callback(1)
        reader.releaseLock()
        break
      }

      chunks.push(value)

      received += value.length

      callback(received / contentSize)
    }

    return new Blob(chunks, { type: 'audio/mp3' })
  }

  /**
   * Delete the given podcastUrl from the indexeddb
   * @param {string} podcastUrl
   * @returns {Promise}
   */
  async deletePodcast(podcastUrl) {
    await this.initPromise

    this.db.transaction('rw', this.dbTable, async () => {
      await this.dbTable.delete(podcastUrl)
    })
  }

  /**
   * Bulk delete the given podcastIds from the indexeddb
   * @param {string[]} podcastIds
   * @returns {Promise}
   */
  async bulkDeletePodcast(podcastUrls) {
    await this.initPromise

    this.db.transaction('rw', this.dbTable, async () => {
      await this.dbTable.bulkDelete(podcastUrls)
    })
  }

  async getSpotifyTrackId(url) {
    // eslint-disable-next-line
    const regex = RegExp('^.*(track\/.*).*$', 'mi')
    let regArray = regex.exec(url)
    return regArray && regArray.length > 1 ? regArray[1].substr(6) : null
  }

  /**
   * Return the anchor environment
   * @param {string} url
   * @returns {string}
   */
  async getAnchorEnvironment(url) {
    // eslint-disable-next-line
    const regex = RegExp('^https:\/\/podcasters.spotify.com\/(.*)\/episodes.*$', 'mi')
    let regArray = regex.exec(url)
    return regArray && regArray.length > 1 ? regArray[1] : null
  }

  /**
   * Return the anchor episode
   * @param {string} url
   * @returns {string}
   */
  async getAnchorEpisode(url) {
    // eslint-disable-next-line
    const regex = RegExp('^https:\/\/podcasters.spotify.com\/.*\/episodes\/(.*)$', 'mi')
    let regArray = regex.exec(url)
    return regArray && regArray.length > 1 ? regArray[1] : null
  }
}

export default PodcastManager
